import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Megaphone, X } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { CampaignCard, SectionLoading, Avatar } from '@/components/community/ui'
import {
  listCommunityCampaigns, joinCommunityCampaign, shareCommunityCampaign, inviteToCommunityCampaign,
  getMembers,
  type CommunityCampaignResponse, type MemberResponse,
} from '@/services/communityApi'

export default function CommunityCampaignsPage() {
  const { t } = useTranslation()
  const { community } = useCommunity()
  const [campaigns, setCampaigns] = useState<CommunityCampaignResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [inviteFor, setInviteFor] = useState<CommunityCampaignResponse | null>(null)

  const load = useCallback(() => {
    if (!community) return
    setLoading(true)
    listCommunityCampaigns(community.id)
      .then(setCampaigns)
      .catch(() => setCampaigns([]))
      .finally(() => setLoading(false))
  }, [community])

  useEffect(() => { load() }, [load])

  const onJoin = async (c: CommunityCampaignResponse) => {
    setBusyId(c.id)
    try { const upd = await joinCommunityCampaign(c.id); setCampaigns((p) => p.map((x) => x.id === upd.id ? upd : x)) } catch { /* ignore */ }
    finally { setBusyId(null) }
  }
  const onShare = async (c: CommunityCampaignResponse) => {
    setBusyId(c.id)
    try { const upd = await shareCommunityCampaign(c.id); setCampaigns((p) => p.map((x) => x.id === upd.id ? upd : x)) } catch { /* ignore */ }
    finally { setBusyId(null) }
  }

  if (!community) return null

  return (
    <CommunityLayout>
      <GovPageTitle title={t('community.campaigns.title')} subtitle={t('community.campaigns.subtitle')} icon={Megaphone} />

      {loading ? <SectionLoading rows={3} /> : campaigns.length === 0 ? (
        <GovEmptyState icon="📣" title={t('community.campaigns.none')} />
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <div key={c.id}>
              <CampaignCard campaign={c} onJoin={onJoin} onShare={onShare} busy={busyId === c.id} />
              <div className="pl-2 -mt-1">
                <button onClick={() => setInviteFor(c)} disabled={busyId === c.id}
                  className="text-sm font-medium text-gray-600 hover:underline disabled:opacity-50 min-h-[40px]">
                  {t('community.actions.invite')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {inviteFor && <InviteModal campaign={inviteFor} members={community.id} onClose={() => setInviteFor(null)} />}
    </CommunityLayout>
  )
}

function InviteModal({ campaign, members: communityId, onClose }: { campaign: CommunityCampaignResponse; members: number; onClose: () => void }) {
  const { t } = useTranslation()
  const [members, setMembers] = useState<MemberResponse[]>([])
  const [selected, setSelected] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getMembers(communityId)
      .then(setMembers)
      .catch(() => setMembers([]))
      .finally(() => setLoading(false))
  }, [communityId])

  const send = async () => {
    if (selected == null) return
    setBusy(true); setError(null)
    try { await inviteToCommunityCampaign(campaign.id, selected); setDone(true) }
    catch (err: any) { setError(err?.response?.data?.message || t('errors.generic')) }
    finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('community.actions.invite')}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <GovCard className="relative w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{t('community.campaigns.inviteTo', { title: campaign.title })}</h2>
          <button onClick={onClose} aria-label={t('community.actions.cancel')} className="p-2 rounded-lg hover:bg-gray-100 min-h-[40px] min-w-[40px] flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <p className="text-sm text-emerald-600 py-4 text-center">{t('community.campaigns.inviteSent')}</p>
        ) : loading ? (
          <p className="text-sm text-gray-500 py-4">{t('common.saving')}</p>
        ) : members.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">{t('community.campaigns.noMembersToInvite')}</p>
        ) : (
          <ul className="divide-y divide-gray-100 max-h-64 overflow-y-auto mb-3">
            {members.map((m) => (
              <li key={m.user.id}>
                <button onClick={() => setSelected(m.user.id)} aria-pressed={selected === m.user.id}
                  className={`w-full flex items-center gap-3 py-2 px-1 text-left min-h-[44px] ${selected === m.user.id ? 'bg-blue-50' : ''}`}>
                  <Avatar name={m.user.fullName} image={m.user.profileImage} size={32} />
                  <span className="text-sm text-gray-700 truncate flex-1">{m.user.fullName}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        {!done && (
          <div className="flex justify-end gap-2">
            <GovButton variant="secondary" size="sm" onClick={onClose}>{t('community.actions.cancel')}</GovButton>
            <GovButton variant="primary" size="sm" onClick={send} disabled={busy || selected == null}>{busy ? t('common.saving') : t('community.actions.sendInvite')}</GovButton>
          </div>
        )}
      </GovCard>
    </div>
  )
}